import { observer } from 'mobx-react-lite'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { FiChevronLeft } from 'react-icons/fi'

// import RecommendRelatedToonsComponent from '../components/toon/RecommendRelatedToonsComponent'
import UserStore from '../stores/UserStore.jsx'
import resize from '../utils/resize.jsx'
import backendApis from '../utils/backendApis'
import AlwayzImage from '../components/molecules/AlwayzImage'
import THImageText from '../components/molecules/THImageText'
import AltoonTagComponent from '../components/templates/AltoonTagComponent'
import RecommendRelatedToonsComponent from '../components/templates/RecommendRelatedToonsComponent'
import { useNavigate, useParams } from 'react-router-dom'
import LoadingIndicator from '../components/atoms/loadingIndicator'
import ToastStore from '../stores/ToastStore'

const AltoonDetailPage = observer(() => {
  const { toonId } = useParams()
  const navigate = useNavigate()

  const rentPeriod = 3
  const scrollContainerRef = useRef(null)
  const [toonInfo, setToonInfo] = useState({})
  const [chapterInfo, setChapterInfo] = useState([])
  const [toonPrice, setToonPrice] = useState(300)
  const [viewedChapterData, setViewedChapterData] = useState([])
  const [openDescriptionModal, setOpenDescriptionModal] = useState(false)
  const [chapterAscendingSort, setChapterAscendingSort] = useState(true)
  const [selectedTab, setSelectedTab] = useState('allChapters')

  const handleTabChange = (newTab) => {
    setSelectedTab(newTab) // 새로운 탭을 상태에 저장
  }

  const dayNameGenerator = (date) => {
    switch (date) {
      case 'Mon':
        return '월'
      case 'Tue':
        return '화'
      case 'Wed':
        return '수'
      case 'Thu':
        return '목'
      case 'Fri':
        return '금'
      case 'Sat':
        return '토'
      case 'Sun':
        return '일'
      default:
        return '월'
    }
  }

  const navigateToReaderPage = ({
    chapter,
    episode = chapter?.episode,
    component = 'detailPage',
    index,
  }) => {
    navigate(`/toon-reader/${chapter?._id}`, {
      state: {
        chapterInfo: chapter,
        toonInfo,
        episode,
        rentPeriod,
      },
    })
  }

  useEffect(() => {
    const fetchData = async () => {
      if (!toonId || toonId === 'undefined') {
        ToastStore.toastOn({
          type: 'error',
          message: '알 수 없는 오류가 발생했어요.\n 잠시후 다시 시도해주세요',
          duration: 2000,
        })
        return
      }
      const result = await backendApis.loadToonDetail(toonId)

      if (result?.status === 200) {
        const viewLog = await backendApis.loadToonViewLog(toonId)
        const chapterList = await backendApis.loadToonChapterInfo(toonId, chapterAscendingSort)

        UserStore.set('currentViewLog', viewLog?.data)
        setViewedChapterData(viewLog?.data?.viewedChapterIdList || [])

        setToonInfo(result?.data[0])
        setToonPrice(result?.data[0]?.toonPrice)
        setChapterInfo(chapterList?.data)
      } else {
        ToastStore.toastOn({
          type: 'error',
          message: '알 수 없는 오류가 발생했어요.\n 잠시후 다시 시도해주세요',
          duration: 2000,
        })
      }
    }

    handleTabChange('allChapters')
    fetchData()

    setTimeout(() => {
      UserStore.setIsLoaded(true)
    }, 500)
  }, [toonId, chapterAscendingSort])

  useEffect(() => {
    const handleScroll = (e) => {
      // 스크롤 위치 기억하는 용도
      window.localStorage.setItem(`detailPage_scrollPosition_${toonId}`, e.target.scrollTop)
    }

    const scrollContainer = scrollContainerRef.current
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  const HeaderComponent = () => {
    return (
      <section className="fixed top-0 bg-[#ffffffe0] w-full z-10 py-2 flex flex-row items-center justify-between overflow-hidden">
        <button
          type="button"
          aria-label="back"
          className="mx-2 p-2 whitespace-nowrap"
          onClick={async () => {
            if (window.history.state.idx === 1) {
              navigate('/')
            } else {
              navigate(-1)
            }
          }}
        >
          <FiChevronLeft className="w-8 h-8 stroke-[0.2vw]" />
        </button>
      </section>
    )
  }

  const ToonInfoSection = () => {
    return (
      <div className="flex flex-col items-center">
        <div className="flex flex-row items-center justify-center rounded-lg">
          <AlwayzImage
            src={resize(toonInfo?.thumbnail, 234, 328, 'fill')}
            alt={toonInfo?.title}
            className="w-[40vw] h-[52vw] rounded-lg flex-shrink-0"
            lazy
          />
          <div className="w-full h-[52vw] flex flex-col pl-2">
            {toonInfo?.bannerImage && (
              <img
                src={toonInfo?.bannerImage}
                alt={toonInfo?.title}
                className="w-full h-[16vh] rounded-t-lg"
              />
            )}
            <div className="p-2 text-xs">
              <div className="text-lg font-semibold">{toonInfo?.title}</div>
              <div className="flex flex-row pb-[4vw]">
                <div>{toonInfo?.author}</div>
                {toonInfo?.weekday && (
                  <div className="text-[#999999] flex flex-row">
                    <div className="mx-2">|</div>
                    {toonInfo?.weekday?.map((day, index) => {
                      const dayString = dayNameGenerator(day)
                      return index === toonInfo.weekday.length - 1
                        ? `${dayString} 연재`
                        : `${dayString}, `
                    })}
                  </div>
                )}
              </div>

              <div className="pb-[4vw] text-[2.8vw]">
                <button
                  type="button"
                  className="flex items-start text-gray-500 text-start max-h-[8vw] overflow-clip"
                  onClick={() => {
                    setOpenDescriptionModal(true)
                  }}
                >
                  <THImageText
                    text={
                      toonInfo?.description?.length > 20
                        ? `${toonInfo?.description?.slice(0, 20)?.replace(/\\n/g, '\n')}..`
                        : toonInfo?.description?.replace(/\\n/g, '\n')
                    }
                  />

                  {toonInfo?.description?.length > 20 && (
                    <img
                      src="/images/toon/ic_detail_arrow.png"
                      alt="arrowDown"
                      className="w-[3.6vw] h-[3.6vw] ml-1"
                    />
                  )}
                </button>
              </div>
              <div className="pb-[2vw] flex flex-row text-[2.4vw] overflow-scroll flex-wrap">
                {toonInfo?.tagList?.hot === true && (
                  <AltoonTagComponent key={Math.random()} tag="hot" type="detailPage" />
                )}
                {toonInfo?.tagList?.new && (
                  <AltoonTagComponent key={Math.random()} tag="new" type="detailPage" />
                )}
                {toonInfo?.tagList?.mainGenre?.map((tag) => (
                  <AltoonTagComponent key={Math.random()} tag={tag} type="detailPage" />
                ))}
                {toonInfo?.tagList?.subGenre?.slice(0, 5).map((tag) => (
                  <AltoonTagComponent key={Math.random()} tag={tag} type="detailPage" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const RelatedToonsTabSection = ({ onTabChange }) => {
    const [selectedType, setSelectedType] = useState(selectedTab)
    const [borderStyle, setBorderStyle] = useState({})

    const allChaptersRef = useRef(null)
    const similarRef = useRef(null)

    // 픽셀(px) 값을 뷰포트 너비(vw)로 변환하는 함수
    const pxToVw = (px) => (px / window.innerWidth) * 100

    // 선택된 탭에 따라 빨간 막대기 위치 및 너비 조정
    useEffect(() => {
      let activeRef
      switch (selectedType) {
        case 'allChapters':
          activeRef = allChaptersRef
          break
        case 'similar':
          activeRef = similarRef
          break
        default:
          break
      }

      if (activeRef && activeRef.current) {
        const rect = activeRef.current.getBoundingClientRect()
        setBorderStyle({
          width: `${pxToVw(rect.width) - 3}vw`, // 가로 너비를 vw로 설정
          left: `${pxToVw(rect.left) - 3}vw`, // 좌측으로 3vw 이동
        })
      }
    }, [selectedType])

    const getButtonStyle = (type) => {
      if (type === selectedType) {
        return 'px-4 text-[#E4443B]' // 선택된 탭 스타일
      }
      return 'px-4 text-gray-500' // 기본 스타일
    }

    const handleTabClick = (type) => {
      if (type === 'similar') {
        window.localStorage.setItem('relatedToonsTabPing', 'true')
      }
      setSelectedType(type) // 애니메이션을 위한 로컬 상태 변경
      setTimeout(() => {
        onTabChange(type) // 애니메이션 완료 후 부모에게 탭 변경 알림
      }, 300) // 애니메이션의 지속 시간과 동일하게 맞춤
    }

    const ping = window.localStorage.getItem('relatedToonsTabPing')

    return (
      <div className="w-full py-[4vw] flex flex-row justify-between items-center text-[4.2vw] font-normal">
        {/* 전체회차/비슷한작품 탭 */}
        <div className="w-full flex flex-row justify-between px-10 py-2 text-[4.2vw] border-b border-[#F3F3F3] relative">
          <div
            style={borderStyle}
            className="absolute -bottom-[0.3vh] h-[0.4vh] bg-[#E4443B] transition-all duration-300"
          />
          <button
            ref={allChaptersRef}
            type="button"
            className={getButtonStyle('allChapters')}
            onClick={() => handleTabClick('allChapters')}
          >
            전체 회차
          </button>
          <button
            ref={similarRef}
            type="button"
            className={getButtonStyle('similar')}
            onClick={() => handleTabClick('similar')}
          >
            비슷한 작품
            {/* // ping 달아주기 */}
            {!ping && (
              <div className="absolute w-[1.5vw] h-[1.5vw] bg-[#E4443B] rounded-full inline-block ml-1 animate-pulse" />
            )}
          </button>
        </div>
      </div>
    )
  }

  const ChapterCountingSection = () => {
    return (
      <>
        {viewedChapterData?.length > 0 && (
          <div className="w-full flex justify-between items-center">
            <div className="text-left">
              전체 {chapterInfo?.length}화{' '}
              <span className="text-[#FF3E3E] ml-[1vw] text-[3.4vw] font-normal">
                무료 {chapterInfo.filter((chapter) => chapter.isFree === true).length}화
              </span>
            </div>

            {/* 정렬버튼 */}
            <button
              type="button"
              className="flex items-center"
              onClick={() => {
                setChapterAscendingSort(!chapterAscendingSort)
              }}
            >
              <img
                src="/images/toon/sortingIcon.png"
                alt="toonCoin"
                className="w-[4vw] h-[4vw] mx-1"
              />
              <div className="text-[3vw] text-[#BCBDC3]">
                {chapterAscendingSort ? '최신순' : '회차순'}
              </div>
            </button>
          </div>
        )}
      </>
    )
  }

  const WeeklyNudge = () => {
    if (toonInfo?.weekday) {
      return (
        <div className="flex pt-[2vw] pb-[4vw] text-[3.6vw] whitespace-nowrap">
          {toonInfo?.weekday?.length === 7 ? '매일 ' : '매주'}
          {toonInfo?.weekday?.length < 7 && (
            <div className="text-[#FF3E3E] font-semibold ml-1">
              {toonInfo?.weekday?.map((day, index) => {
                const dayString = dayNameGenerator(day)
                return index === toonInfo.weekday.length - 1
                  ? `${dayString}요일마다 `
                  : `${dayString}, `
              })}
            </div>
          )}
          <div className="ml-1">새로운 회차가 업데이트돼요</div>
        </div>
      )
    }
  }

  const NoticeComponent = () => {
    return (
      <div className="flex items-center w-full pl-[2vw] py-2 mt-2 mb-4 text-[3.8vw] bg-[#F6F6FA] font-semibold rounded">
        <img
          src="/images/toon/megaphoneBlue.png"
          alt="megaphoneBlack"
          className="w-[4vw] h-[4vw] mr-2"
        />
        {toonInfo?.notice}
      </div>
    )
  }

  // 회차 목록
  const ChapterList = () => {
    return (
      <div className="pb-10">
        {/* 공개일 이후인 회차 표시 */}
        {chapterInfo
          ?.filter((chapter) => moment().isAfter(chapter?.publishedAt))
          .map((chapter) => (
            <button
              type="button"
              className={`relative my-2 flex flex-row rounded-lg border-b border-[#f5f5f5] ${
                viewedChapterData?.some(
                  (item) => item.chapterId.toString() === chapter._id && !item.isBulkPurchased
                )
                  ? 'bg-gray-100'
                  : ''
              }`}
              key={chapter._id || chapter?.episode}
              onClick={() => {
                if (chapter.isFree === false) {
                  UserStore.setBottomsheetOpened(true)
                } else if (chapter.episode > 9) {
                  UserStore.setBottomsheetOpened(true)
                } else {
                  navigateToReaderPage({
                    chapter,
                    episode: chapter?.episode,
                    component: 'detailPage_chapter',
                  })
                }
              }}
            >
              <div className="relative">
                <AlwayzImage
                  src={resize(
                    chapter.detailThumbnail,
                    toonInfo?.verticalThumbnail ? 360 : 234,
                    toonInfo?.verticalThumbnail ? 486 : 196,
                    'fill'
                  )}
                  alt={chapter.chapterTitle}
                  className={
                    toonInfo?.verticalThumbnail
                      ? 'w-[30vw] h-[43vw] rounded-lg resize-y self-stretch'
                      : 'w-[30vw] h-[25vw] rounded-lg resize-y self-stretch'
                  }
                  lazy
                />

                {moment().isAfter(
                  moment(UserStore?.currentViewLog?.waitForFreeInfo?.eventStartedAt)
                ) &&
                  moment().isBefore(
                    moment(UserStore?.currentViewLog?.waitForFreeInfo?.eventEndedAt)
                  ) &&
                  chapterInfo?.filter((chapter) => moment().isAfter(chapter?.publishedAt))?.length -
                    UserStore?.currentViewLog?.waitForFreeInfo?.recentChapterLimit >=
                    chapter?.episode && (
                    <img
                      src="/images/toon/waitForFreeIcon.png"
                      alt="ticket"
                      className="absolute bottom-[1vw] right-[1vw] w-[13vw] h-auto"
                    />
                  )}

                {chapter?.onlyPaidCoin && (
                  <img
                    src="/images/toon/onlyPaidCoinIcon.png"
                    alt="ticket"
                    className="absolute top-[1vw] right-[1vw] w-[6vw] h-[6vw]"
                  />
                )}
                {moment().format('YYYY-MM-DD') ===
                  moment(chapter?.publishedAt).format('YYYY-MM-DD') && (
                  <img
                    src="/images/toon/upIcon.png"
                    alt="ticket"
                    className="absolute top-[1vw] left-[1vw] w-[5vw] h-[3vw]"
                  />
                )}
              </div>
              <div className="p-4 w-[60vw] h-[12vh] flex flex-col items-start justify-center font-semibold text-[3.8vw]">
                <div
                  className={`text-start ${
                    viewedChapterData?.some((item) => item.chapterId.toString() === chapter._id)
                      ? 'text-gray-400'
                      : ''
                  }`}
                >
                  {chapter.chapterTitle}
                </div>
                <div
                  className={`pt-1 flex flex-row items-center text-[3vw] font-medium ${
                    viewedChapterData?.some((item) => item.chapterId.toString() === chapter._id)
                      ? 'text-gray-400'
                      : 'text-gray-600'
                  }`}
                >
                  {chapter.chapterRatingCount !== 0 && (
                    <div className="flex flex-row items-center justify-end mr-2">
                      <img
                        alt=""
                        style={{
                          marginRight: 2,
                          width: '2.4vw',
                          height: '2.4vw',
                        }}
                        src="/images/toon/star.png"
                      />
                      {Math.round(
                        (chapter?.chapterRatingScore / chapter?.chapterRatingCount) * 100
                      ) / 100 || 0}
                    </div>
                  )}
                  <div className="">{moment(chapter.publishedAt).format('YY.MM.DD')}</div>
                  <div className="flex items-center ml-1">
                    {chapter.isFree === false &&
                    // moment().isAfter(
                    //   moment(chapter.publishedAt).add(previewDays, 'days'),
                    // ) &&
                    !viewedChapterData?.includes(chapter._id) ? (
                      <>
                        <img
                          src="/images/toon/toonCoin.png"
                          alt="toonCoin"
                          className="w-[4vw] h-[4vw] ml-1"
                        />
                        <div className="ml-1">{toonPrice}C</div>
                      </>
                    ) : (
                      ''
                    )}
                  </div>
                </div>
              </div>
              {!chapter.isFree &&
                viewedChapterData?.some(
                  (item) =>
                    item.chapterId.toString() === chapter._id &&
                    moment()
                      .startOf('day')
                      .diff(moment(item.firstViewedAt).startOf('day'), 'days') > rentPeriod
                ) && (
                  <div className="absolute mt-[18vw] right-[6vw] p-2 text-xs text-gray-600 rounded-tr rounded-bl">
                    열람 기간만료
                  </div>
                )}
              {!chapter.isFree &&
                viewedChapterData &&
                !viewedChapterData?.some(
                  (item) =>
                    item.chapterId.toString() === chapter._id &&
                    moment()
                      .startOf('day')
                      .diff(moment(item.firstViewedAt).startOf('day'), 'days') > rentPeriod
                ) && (
                  <div className="absolute mt-[18vw] right-[6vw] p-2 text-[2.4vw] text-gray-600">
                    {(() => {
                      const viewedChapter = viewedChapterData?.find(
                        (item) => item.chapterId.toString() === chapter._id
                      )
                      if (viewedChapter && viewedChapter.firstViewedAt) {
                        const dateStr = moment(viewedChapter.firstViewedAt)
                          .startOf('D')
                          .add(3, 'days')
                          .format('M월 D일')
                        return `${dateStr}까지 무료열람`
                      }
                      return null // 아무 것도 반환하지 않을 때
                    })()}
                  </div>
                )}
            </button>
          ))}
      </div>
    )
  }

  const ScrollButton = () => {
    return (
      <div type="button" className="w-[8vw] fixed bottom-[10vh] right-4">
        <button
          type="button"
          className="rounded-full bg-white shadow-md"
          onClick={() => {
            if (scrollContainerRef.current) {
              scrollContainerRef.current.scrollTop = 0
            }
          }}
        >
          <img src="/images/toon/toTopIcon.png" alt="up" className="w-full" />
        </button>
        <button
          type="button"
          className="rounded-full bg-white shadow-md"
          onClick={() => {
            if (scrollContainerRef.current) {
              scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight
            }
          }}
        >
          <img src="/images/toon/toBottomIcon.png" alt="up" className="w-full" />
        </button>
      </div>
    )
  }

  const ToonDescriptionModal = () => {
    return (
      <>
        {openDescriptionModal && (
          <div className="fixed inset-0 z-30 flex items-center justify-center">
            <button
              type="button"
              className="fixed top-0 w-full h-full bg-black bg-opacity-50"
              onClick={() => setOpenDescriptionModal(false)}
            />
            <div className="bg-white w-[90vw] rounded-lg p-4 max-h-[80vh] min-h-[20vh] overflow-y-auto z-40">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold">{toonInfo?.title}</h2>
                <button type="button" onClick={() => setOpenDescriptionModal(false)} className="">
                  <img
                    src="/image/general/basicIcon/close/gray.png"
                    alt="close"
                    className="w-[10vw] h-[10vw]"
                  />
                </button>
              </div>
              <div className="text-[3.6vw] whitespace-pre-wrap">
                {toonInfo?.description?.replace(/\\n/g, '\n')}
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

  if (!UserStore.isLoaded) {
    return <LoadingIndicator isLoading />
  }

  return (
    <div
      className="w-full overflow-y-scroll pt-[10vh]"
      style={{ height: '100vh' }}
      ref={scrollContainerRef}
    >
      <HeaderComponent />
      {chapterInfo?.length > 0 && (
        <div className="px-4">
          <ToonInfoSection />
          <RelatedToonsTabSection onTabChange={handleTabChange} />
          {selectedTab === 'allChapters' && (
            <>
              <ChapterCountingSection />
              {toonInfo?.notice?.length > 0 && <NoticeComponent />}
              <WeeklyNudge />
              <ChapterList />
            </>
          )}
          {selectedTab === 'similar' && (
            <>
              <RecommendRelatedToonsComponent toonInfo={toonInfo} type="relatedToonsDetail" />
            </>
          )}
        </div>
      )}
      <ScrollButton />
      <ToonDescriptionModal />
    </div>
  )
})

export default AltoonDetailPage
